import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { EcosystemOpenIssue } from '../../types/agents';

interface OpenIssuesTableProps {
  issues: EcosystemOpenIssue[];
}

function formatAge(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const days = Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  return `${days}d`;
}

const PRIORITY_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  '1': 'error',
  '2': 'warning',
  '3': 'default',
};

export function OpenIssuesTable({ issues }: OpenIssuesTableProps) {
  const sorted = [...issues].sort((a, b) => {
    const aPri = parseInt(a.priority ?? '9', 10);
    const bPri = parseInt(b.priority ?? '9', 10);
    if (aPri !== bPri) return aPri - bPri;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Open agent issues ({issues.length})
      </Typography>
      {issues.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Queue empty.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Title</TableCell>
                <TableCell align="right">Age</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((issue) => (
                <TableRow key={issue.number} hover>
                  <TableCell>
                    <Link href={issue.url} target="_blank" rel="noreferrer">
                      #{issue.number}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {issue.priority ? (
                      <Chip
                        label={`P${issue.priority}`}
                        size="small"
                        color={PRIORITY_COLOR[issue.priority] ?? 'default'}
                      />
                    ) : (
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        —
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                      {issue.agent ?? '—'}
                    </Box>
                  </TableCell>
                  <TableCell>{issue.title}</TableCell>
                  <TableCell align="right">{formatAge(issue.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
